import { useCallback, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  LinearProgress,
  Alert,
  Button,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { Close as CloseIcon, OpenInNew as OpenInNewIcon } from "@mui/icons-material";

import { JOB_TYPE_DESIGN, STATUS_BG_SUBTLE, MONO_SX, CHIP_GEOMETRIC_SX } from "./jobs-design-tokens";
import { useBackgroundJobs } from "@/contexts/BackgroundJobsContext";
import { restartJob } from "@/services/jobs-api";
import type { TrackedJob } from "@/types/jobs";
import {
  JobDetailDialog,
  STATUS_LABEL,
  STATUS_COLOR,
  JOB_TYPE_LABEL,
  formatTime,
} from "./JobDetailDialog";
import { JobHistoryTab } from "./JobHistoryTab";

interface Props {
  open: boolean;
  onClose: () => void;
}

// ─── Job Card ─────────────────────────────────────────────────────────────────

function JobCard({ job }: { job: TrackedJob }) {
  const { addJob } = useBackgroundJobs();
  const [detailOpen, setDetailOpen] = useState(false);

  const isActive = job.status === "Queued" || job.status === "Running";
  const isCompleted = job.status === "Completed";
  const isFailed = job.status === "Failed";
  const typeDesign = JOB_TYPE_DESIGN[job.jobType] ?? { primary: "#94a3b8", bg: "rgba(148,163,184,0.06)", border: "rgba(148,163,184,0.25)", text: "#64748b" };
  const typeAccent = typeDesign.primary;

  const handleRetry = useCallback(async () => {
    try {
      const { jobId } = await restartJob(job.id);
      addJob(jobId, job.jobType, job.entityId, job.service);
    } catch {
      // silencioso
    }
  }, [job, addJob]);

  return (
    <>
      <Box
        onClick={isCompleted ? () => setDetailOpen(true) : undefined}
        sx={{
          py: 1.5,
          px: 2,
          mx: 1.5,
          my: 0.75,
          borderRadius: "10px",
          border: "1px solid",
          borderColor: "divider",
          borderLeft: "4px solid",
          borderLeftColor: typeAccent,
          bgcolor: isCompleted ? "rgba(46,125,50,0.08)" : typeDesign.bg,
          cursor: isCompleted ? "pointer" : "default",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          "&:hover": isCompleted ? { bgcolor: "rgba(46,125,50,0.16)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" } : undefined,
          transition: "background-color 0.15s, box-shadow 0.15s",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: isCompleted ? "success.dark" : typeDesign.text }}>
            {JOB_TYPE_LABEL[job.jobType] ?? job.jobType}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isCompleted && (
              <OpenInNewIcon sx={{ fontSize: 14, color: "text.disabled" }} />
            )}
            <Chip
              label={STATUS_LABEL[job.status] ?? job.status}
              color={STATUS_COLOR[job.status] ?? "default"}
              size="small"
              sx={{ fontSize: "0.7rem", ...CHIP_GEOMETRIC_SX }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography variant="caption" sx={{ ...MONO_SX, color: "text.disabled" }}>
            #{job.entityId}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
            {job.completedAt
              ? formatTime(job.completedAt)
              : job.startedAt
              ? `iniciado ${formatTime(job.startedAt)}`
              : ""}
          </Typography>
        </Box>

        {isActive && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant={job.progressPercent != null ? "determinate" : "indeterminate"}
              value={job.progressPercent ?? undefined}
              sx={{ borderRadius: 1, height: 6 }}
            />
            {job.progressMessage && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {job.progressMessage}
              </Typography>
            )}
          </Box>
        )}

        {isFailed && job.errorMessage && (
          <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="caption">{job.errorMessage}</Typography>
          </Alert>
        )}

        {isFailed && (
          <Button
            size="small"
            sx={{ mt: 1, textTransform: "none" }}
            onClick={(e) => { e.stopPropagation(); handleRetry(); }}
          >
            Reintentar
          </Button>
        )}

        {isCompleted && (
          <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: "block" }}>
            Click para ver detalles
          </Typography>
        )}
      </Box>

      {detailOpen && (
        <JobDetailDialog job={job} onClose={() => setDetailOpen(false)} />
      )}
    </>
  );
}

// ─── Panel principal ──────────────────────────────────────────────────────────

export function JobResultsPanel({ open, onClose }: Props) {
  const { jobs, clearCompleted } = useBackgroundJobs();
  const [activeTab, setActiveTab] = useState(0);

  const hasFinished = jobs.some((j) => j.status === "Completed" || j.status === "Failed");

  const sorted = [...jobs].sort((a, b) => {
    const aActive = a.status === "Queued" || a.status === "Running";
    const bActive = b.status === "Queued" || b.status === "Running";
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
    const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
    return bTime - aTime;
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 480, display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "rgba(0,0,0,0.02)",
          }}
        >
          <Typography variant="h6" fontSize="1rem" fontWeight={600}>
            Procesos en segundo plano
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {activeTab === 0 && hasFinished && (
              <Button size="small" sx={{ textTransform: "none" }} onClick={clearCompleted}>
                Limpiar completados
              </Button>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40, "& .MuiTabs-indicator": { bgcolor: "#0891b2" } }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          <Tab label="En curso" sx={{ minHeight: 40, textTransform: "none", fontSize: "0.85rem" }} />
          <Tab label="Histórico" sx={{ minHeight: 40, textTransform: "none", fontSize: "0.85rem" }} />
        </Tabs>

        {/* Tab: En curso */}
        {activeTab === 0 && (
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {sorted.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No hay procesos recientes
                </Typography>
              </Box>
            ) : (
              sorted.map((job) => <JobCard key={job.id} job={job} />)
            )}
          </Box>
        )}

        {/* Tab: Histórico */}
        {activeTab === 1 && <JobHistoryTab />}
      </Box>
    </Drawer>
  );
}
